import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, Loader2, Tag } from "lucide-react";
import DOMPurify from "isomorphic-dompurify";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchKnowledgeArticle } from "@/lib/content";
import { RelatedArticles } from "@/components/site/RelatedArticles";
import { ContextualServiceLinks } from "@/components/site/ContextualServiceLinks";
import { jsonLdScript } from "@/lib/jsonld";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { buildPostFaqs } from "@/lib/post-faqs";

function toKeywords(v: any): string[] {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === "string") {
    try { const j = JSON.parse(v); return Array.isArray(j) ? j.map(String) : []; }
    catch { return v.split(",").map(s => s.trim()).filter(Boolean); }
  }
  return [];
}

export const Route = createFileRoute("/knowledge-hub/$slug")({
  loader: async ({ params }) => ({ post: await fetchKnowledgeArticle(params.slug) }),
  head: ({ params, loaderData }) => {
    const post: any = (loaderData as any)?.post ?? null;
    const url = `https://onlinetaxvat.com/knowledge-hub/${params.slug}`;
    const fallbackTitle = params.slug.split("-").map((w) => w ? w[0].toUpperCase() + w.slice(1) : w).join(" ");
    const rawTitle: string = post?.meta_title || post?.title || fallbackTitle;
    const SUFFIX = " — Knowledge Hub";
    const room = 60 - SUFFIX.length;
    const title = rawTitle.length > room ? `${rawTitle.slice(0, room - 1).trimEnd()}…` : rawTitle;
    const rawDesc: string = post?.meta_description ||
      `Expert NBR-aligned guide on ${rawTitle} — practical tax and VAT compliance advice for Bangladesh.`;
    const description = rawDesc.length > 158 ? `${rawDesc.slice(0, 155).trimEnd()}…` : rawDesc;
    const bodyLen = String(post?.content || "").replace(/<[^>]*>/g, "").trim().length;
    const isThin = !post || bodyLen < 300;
    const meta: any[] = [
      { title: `${title}${SUFFIX}` },
      { name: "description", content: description },
      { property: "og:title", content: rawTitle },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: rawTitle },
      { name: "twitter:description", content: description },
    ];
    if (isThin) {
      meta.push({ name: "robots", content: "noindex, nofollow" });
    }
    if (post?.cover_url) {
      meta.push({ property: "og:image", content: post.cover_url });
      meta.push({ name: "twitter:image", content: post.cover_url });
    }
    const scripts: any[] = [];
    if (post) {
      scripts.push(
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: rawTitle,
          description,
          articleSection: post.category || undefined,
          image: [post.cover_url || "https://onlinetaxvat.com/pwa/icon-512x512.png?v=5"],
          datePublished: post.published_at ?? new Date().toISOString(),
          dateModified: post.updated_at ?? post.published_at ?? new Date().toISOString(),
          author: { "@type": "Organization", "@id": "https://onlinetaxvat.com/#organization", name: "Online Tax VAT Bangladesh" },
          publisher: {
            "@type": "Organization",
            "@id": "https://onlinetaxvat.com/#organization",
            name: "Online Tax VAT Bangladesh",
            logo: {
              "@type": "ImageObject",
              url: "https://onlinetaxvat.com/pwa/icon-512x512.png?v=5",
              width: 512,
              height: 512,
            },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
        }),
      );
      scripts.push(
        jsonLdScript({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://onlinetaxvat.com/" },
            { "@type": "ListItem", position: 2, name: "Knowledge Hub", item: "https://onlinetaxvat.com/knowledge-hub" },
            { "@type": "ListItem", position: 3, name: rawTitle, item: url },
          ],
        }),
      );
    }
    return { meta, links: [{ rel: "canonical", href: url }], scripts };
  },
  component: KnowledgeArticlePage,
  errorComponent: ({ error }) => (
    <SiteShell><div className="container-page py-32 text-center text-muted-foreground">{error.message}</div></SiteShell>
  ),
  notFoundComponent: () => (
    <SiteShell>
      <div className="container-page py-32 text-center">
        <h1 className="font-display text-3xl font-bold">Article Not Found</h1>
        <Button asChild className="mt-6"><Link to="/knowledge-hub">Back to Knowledge Hub</Link></Button>
      </div>
    </SiteShell>
  ),
});

function KnowledgeArticlePage() {
  const { slug } = Route.useParams();
  const loaderData = Route.useLoaderData() as { post: any } | undefined;
  const initialPost = loaderData?.post ?? null;
  const { data: post = initialPost, isLoading } = useQuery({
    queryKey: ["knowledge_article", slug],
    queryFn: () => fetchKnowledgeArticle(slug),
    initialData: initialPost ?? undefined,
  });

  if (isLoading) {
    return <SiteShell><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></SiteShell>;
  }
  if (!post) {
    return (
      <SiteShell>
        <div className="container-page py-32 text-center">
          <h1 className="font-display text-3xl font-bold">Article Not Found</h1>
          <Button asChild className="mt-6"><Link to="/knowledge-hub">Back to Knowledge Hub</Link></Button>
        </div>
      </SiteShell>
    );
  }

  const keywords = toKeywords((post as any).seo_keywords);
  const body = String((post as any).content || "");
  const looksLikeHtml = /<\/?[a-z][^>]*>/i.test(body);
  // Enforce a single <h1> per page: demote any h1s in body content to h2
  // so the article title remains the sole h1, preserving SEO hierarchy.
  const normalizedHtml = looksLikeHtml
    ? body.replace(/<(\/?)h1(\s|>)/gi, "<$1h2$2")
    : body.split(/\n{2,}/).map(p => `<p>${p.replace(/\n/g, "<br/>")}</p>`).join("");
  const safeHtml = DOMPurify.sanitize(normalizedHtml);

  return (
    <SiteShell>
      <article className="container-page py-14 md:py-20 max-w-3xl">
        <Button asChild variant="ghost" size="sm" className="mb-6 -ml-2">
          <Link to="/knowledge-hub"><ArrowLeft className="mr-1 h-4 w-4" /> Knowledge Hub</Link>
        </Button>
        {post.category && (
          <Badge variant="secondary" className="mb-3 bg-primary/10 text-primary border-primary/20">{post.category}</Badge>
        )}
        <h1 className="font-display font-bold text-3xl md:text-4xl leading-tight">{post.title}</h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          {post.published_at && (
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" />
              {new Date(post.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </span>
          )}
          {keywords.length > 0 && (
            <span className="inline-flex items-center gap-1.5"><Tag className="h-4 w-4" /> {keywords.slice(0, 4).join(", ")}</span>
          )}
        </div>
        {post.cover_url && (
          <img src={post.cover_url} alt={`Online Tax VAT Bangladesh Knowledge Hub — ${post.title} (Income Tax Return, VAT & Trade License Guide)`} loading="eager" decoding="async" className="mt-8 w-full rounded-2xl aspect-[16/9] object-cover" />
        )}
        <div
          className="prose prose-neutral dark:prose-invert max-w-none mt-8 prose-headings:font-display prose-headings:font-bold prose-h2:text-2xl prose-h3:text-xl prose-a:text-primary [content-visibility:auto] [contain-intrinsic-size:1px_1200px]"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />
        {keywords.length > 0 && (
          <div className="mt-12 pt-6 border-t flex flex-wrap gap-2">
            {keywords.map((k) => (
              <Badge key={k} variant="outline" className="text-xs">{k}</Badge>
            ))}
          </div>
        )}
        <ContextualServiceLinks title={post.title} body={body} category={post.category} />
        <SeoFaqBlock
          title="Frequently Asked Questions"
          faqs={buildPostFaqs({ slug, title: post.title, category: post.category, tags: keywords })}
        />
        <RelatedArticles currentSlug={slug} source="knowledge-hub" />
      </article>
    </SiteShell>
  );
}
